import {
  IconDotsVertical,
  IconLogout,
  IconMoon,
  IconSun,
  IconPaletteFilled,
} from '@tabler/icons-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useTheme } from '@/components/theme-provider';
import { useAuth } from '@/hooks/useAuth';
import { useState, useEffect } from 'react';
import { themes, type Theme } from '@/components/theme/ThemeSelector';
import { applyColorTheme } from '@/lib/theme-init';

export function NavUser({
  user,
}: {
  user: {
    full_name: string;
    phone_number: string;
    avatar: string;
    role: string;
  };
}) {
  const { isMobile } = useSidebar();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { logout } = useAuth();
  const [currentColorTheme, setCurrentColorTheme] = useState<Theme>('default');

  useEffect(() => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('color-theme') as Theme;
    if (savedTheme && themes.find(t => t.id === savedTheme)) {
      setCurrentColorTheme(savedTheme);
      // Theme is already applied in main.tsx, just sync the state
    }
  }, []);

  const selectColorTheme = (themeId: Theme) => {
    setCurrentColorTheme(themeId);
    applyColorTheme(themeId);
    localStorage.setItem('color-theme', themeId);
  };

  const toggleTheme = () => {
    const currentTheme = resolvedTheme || theme;
    setTheme(currentTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              data-testid="user-menu-trigger"
            >
              <Avatar className="h-8 w-8 rounded-lg grayscale">
                <AvatarImage src={user.avatar} alt={user.full_name} />
                <AvatarFallback className="rounded-lg">
                  {user.full_name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.full_name}</span>
                <span className="text-muted-foreground truncate text-xs">
                  {user.role}
                </span>
              </div>
              <IconDotsVertical className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.full_name} />
                  <AvatarFallback className="rounded-lg">
                    {user.full_name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.full_name}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {user.phone_number}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {/*
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <IconUserCircle />
                Account
              </DropdownMenuItem>
              {authUser?.role?.includes('super_admin') && (
                <DropdownMenuItem>
                  <IconCreditCard />
                  Billing
                </DropdownMenuItem>
              )}
              <DropdownMenuItem>
                <IconNotification />
                Notifications
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <IconPaletteFilled className="mr-2 h-4 w-4" />
                Theme
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-64">
                <DropdownMenuItem
                  onClick={toggleTheme}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    toggleTheme();
                  }}
                  data-testid="theme-toggle"
                  className="cursor-pointer"
                >
                  {(resolvedTheme || theme) === 'dark' ? <IconSun className="mr-2 h-4 w-4" /> : <IconMoon className="mr-2 h-4 w-4" />}
                  {(resolvedTheme || theme) === 'dark' ? 'Light mode' : 'Dark mode'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Color Themes</DropdownMenuLabel>
                {themes.map((colorTheme) => (
                  <DropdownMenuItem
                    key={colorTheme.id}
                    onClick={() => selectColorTheme(colorTheme.id)}
                    className="flex flex-col items-start gap-1 p-2"
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className={`w-2 h-2 rounded-full border ${
                          currentColorTheme === colorTheme.id ? 'bg-primary border-primary' : 'border-muted-foreground'
                        }`} 
                      />
                      <span className="text-sm">{colorTheme.name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-4">{colorTheme.description}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              onTouchEnd={(e) => {
                e.preventDefault();
                logout();
              }}
              data-testid="logout-btn"
              className="cursor-pointer"
            >
              <IconLogout />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
